import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import classNames from 'classnames'
import React from 'react'

export interface WorkOrderTotalFooterProps {
  label: string
  /** e.g. ["Prep: 6", "Painting: 18", "Total: 24"] — joined by thin vertical rules. */
  parts: string[]
  /** Extra class on the tray, so a caller can add its own hover tint / spacing. */
  className?: string
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const outline = `1px solid ${neutral.P85}`
    return {
      // The bar sits in a thin tray, so its own outline reads inside whatever contains it.
      footerTray: {
        padding: theme.spacing(0.5)
      },
      footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minHeight: 44,
        padding: theme.spacing(0, 1.5, 0, 2),
        border: outline,
        // `lg` (8px) is the closest token to the mock's 6px footer bar.
        borderRadius: theme.borderRadius.lg,
        backgroundColor: neutral.White,
        // With the label gone on phones (see footerLabel), the breakdown centers in the bar.
        [theme.breakpoints.down('xs')]: {
          justifyContent: 'center',
          padding: theme.spacing(0, 1.5)
        }
      },
      footerLabel: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: neutral.P40,
        // Phones drop it: the breakdown already ends in "Total: N", and on a narrow card the leading
        // label is what pushed that number off the end of the bar.
        [theme.breakpoints.down('xs')]: {
          display: 'none'
        }
      },
      footerTotal: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: neutral.P40,
        whiteSpace: 'nowrap'
      },
      // "Prep: 4 | Painting: 29 | Total: 33" — the parts joined by thin vertical rules (matches the mock).
      footerBreakdown: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexShrink: 0,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('xs')]: {
          justifyContent: 'center'
        }
      },
      footerDivider: {
        width: 1,
        height: 11,
        borderRadius: theme.borderRadius.full,
        backgroundColor: neutral.P80,
        flexShrink: 0
      }
    }
  },
  { name: 'WorkOrderTotalFooter' }
)

/**
 * The Work Order's "Total — Prep: X | Painting: Y | Total: Z" bar, shared by the hour cards
 * (`WorkOrderCardNext`) and the Work Order Options table (`OptionsTableNext`) so the two read as one
 * system (ENG-1850). Purely presentational: each caller works out its own parts.
 */
export default function WorkOrderTotalFooter({ label, parts, className }: WorkOrderTotalFooterProps) {
  const classes = useStyles()
  return (
    <div className={classNames(classes.footerTray, className)}>
      <div className={classes.footer}>
        <span className={classes.footerLabel}>{label}</span>
        <span className={classes.footerBreakdown}>
          {/* Index keys — an account-configurable label (rename labour to "Prep") can make two parts
              render the same string. */}
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className={classes.footerDivider} />}
              <span className={classes.footerTotal}>{part}</span>
            </React.Fragment>
          ))}
        </span>
      </div>
    </div>
  )
}
